import { scrollToRef } from 'front-end/src/utils/scrollHelpers'
import React, { createRef, FC, useEffect, useRef } from 'react'
import { Plural, t } from '@lingui/macro'

import { useCachedWorkspacesQuery } from 'front-end/src/app/stores/useCachedWorkspacesQuery'
import { useLocation } from 'react-router-dom'

import CardGrid from '../../../../components/common/card-grid/CardGrid'
import Loader from '../../../../components/common/loader/Loader'
import { PageContentWrapper } from '../../../../components/common/page/Page'
import WorkspaceCard from '../../workspace-card/WorkspaceCard'

import './YourWorkspaces.less'
import { buildWorkspaceCategoryTree } from 'front-end/src/pages/workspace-page/buildWorkspaceCategoryTree'
import { Box } from 'front-end/src/components'
import { ExpandableCardGrid } from 'front-end/src/components/common/expandable-card-grid/ExpandableCardGrid'

export const YourWorkspaces: FC = () => {
  const { data, loading } = useCachedWorkspacesQuery()
  const categoryRefs = useRef<Record<string, any>>()
  const { hash } = useLocation()
  const hashCategoryId = hash.replace(/^#/, '') || ''

  const getAnchorRef = (hash = hashCategoryId) =>
    categoryRefs?.current?.[hash]?.current

  useEffect(() => {
    const ref = getAnchorRef()
    if (ref && !loading) {
      scrollToRef(ref)
    }
  }, [hash, loading])

  if (loading) {
    return <Loader />
  }

  if (!data || data?.workspaces.length === 0) {
    return null
  }

  const categoryTree = buildWorkspaceCategoryTree(data?.workspaces)

  if (!categoryRefs.current) {
    categoryRefs.current = {}
    categoryTree.categories.forEach((category) => {
      if (categoryRefs.current) {
        categoryRefs.current[generateAnchorId(category.id)] = createRef()
      }
    })
  }

  const WorkspacesGrid = ({
    workspaces,
    isExpandable = false,
    title,
    copyAnchorId
  }: {
    workspaces: any
    isExpandable?: boolean
    title: string
    copyAnchorId?: string
  }) => {
    const cards = workspaces.map((workspace, idx) => {
      let image: string | null | undefined = null

      if (workspace.coverImage) {
        const { cdnUrl, imageVariants } = workspace.coverImage

        const imageVariant = imageVariants.find(
          (variant) => variant.size_key === '600x300'
        )

        image = imageVariant ? imageVariant.cdnUrl : cdnUrl
      }

      return (
        <WorkspaceCard
          key={idx}
          id={workspace.id}
          title={workspace.name}
          itemsCount={workspace.publishedTopicsCount}
          image={image}
        />
      )
    })

    if (isExpandable) {
      return (
        <Box
          className="YourWorkspacesGrid"
          ref={copyAnchorId && categoryRefs.current?.[copyAnchorId]}
          id={copyAnchorId}
        >
          <ExpandableCardGrid
            title={title}
            copyAnchorId={copyAnchorId}
            cards={cards}
            limit={11}
            renderShowMore={(remainingCards) => {
              return (
                <Plural
                  value={remainingCards}
                  one="Show # more workspace"
                  other="Show # more workspaces"
                />
              )
            }}
          />
        </Box>
      )
    }

    return (
      <Box className="YourWorkspacesGrid">
        <Box className="grid-header">
          <h3>{title}</h3>
        </Box>
        <CardGrid cards={cards} />
      </Box>
    )
  }

  return (
    <PageContentWrapper className="YourWorkspaces">
      {categoryTree.categories.map((category) => (
        <WorkspacesGrid
          key={category.id}
          title={category.name}
          workspaces={category.workspaces}
          isExpandable
          copyAnchorId={generateAnchorId(category.id)}
        />
      ))}

      {categoryTree.workspacesWithoutCategories.length > 0 && (
        <WorkspacesGrid
          title={
            categoryTree.categories.length > 0
              ? t`Other workspaces`
              : t`Your workspaces`
          }
          workspaces={categoryTree.workspacesWithoutCategories}
        />
      )}
    </PageContentWrapper>
  )
}

const generateAnchorId = (categoryId) => `category-${categoryId}`
